// Ablage: eingefrorene Signale aus dem Scanner, gesammelt ins Journal uebernehmen (Spec 4.3)
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../api/client";
import { de, num } from "./common";

export const useAblage = () => useQuery({
  queryKey: ["ablage", "offen"],
  queryFn: () => apiClient.get("/ablage", { params: { status: "offen" } }).then(r => (Array.isArray(r.data) ? r.data : [])),
});

const when = (iso: string) => new Date(iso).toLocaleString("de-DE", { timeZone: "Europe/Berlin", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }).replace(",", "");

export default function AblageTab({ onTaken }: { onTaken: (n: number) => void }) {
  const qc = useQueryClient();
  const { data: items = [], isLoading } = useAblage();
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const toggle = (id: number) => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const ids = items.filter((a: any) => sel.has(a.id)).map((a: any) => a.id);
  const run = async (path: string) => {
    setBusy(true); setMsg(null);
    try {
      const r = await apiClient.post(`/ablage/${path}`, { ids });
      setSel(new Set());
      qc.invalidateQueries({ queryKey: ["ablage"] }); qc.invalidateQueries({ queryKey: ["journal"] });
      if (path === "uebernehmen") onTaken(r.data?.uebernommen?.length ?? 0);
    } catch (e: any) {
      setMsg(e?.response?.data?.detail || "Hat nicht geklappt");
    } finally { setBusy(false); }
  };

  return (
    <>
      <p className="j-intro">Eingefrorene Signale. Auswählen und gesammelt ins Journal übernehmen.</p>
      <div className="j-list">
        {isLoading && <div className="sc-empty">Lädt …</div>}
        {!isLoading && items.length === 0 && <div className="sc-empty">Ablage ist leer. Im Scanner eine Zeile lange drücken, um ein Signal abzulegen.</div>}
        {items.map((a: any) => {
          const s = a.snapshot || {}, mc = s.market_context || {}, live = s.live || {};
          const long = a.direction !== "short";
          const px = num(live.price_ref ?? s.entry);
          const vol = num(mc.sym_vol_ratio ?? s.scoring_details?.rvol);
          const adx = num(live.adx_live ?? mc.symbol_adx);
          const meta = [px !== null ? `Kurs ${de(px)}` : null, vol !== null ? `Vol ${de(vol)}` : null, adx !== null ? `ADX ${Math.round(adx)}` : null].filter(Boolean).join(" · ");
          return (
            <label key={a.id} className="sc-row ab-row">
              <input type="checkbox" checked={sel.has(a.id)} onChange={() => toggle(a.id)} aria-label={`${s.name || a.symbol} auswählen`} />
              <span className={`sc-dir ${long ? "up" : "down"}`} aria-label={long ? "Long" : "Short"}>{long ? "▲" : "▼"}</span>
              <div className="sc-body">
                <div className="sc-name"><span className="sc-company">{s.name || a.symbol}</span><span className="sc-sym mono">{a.symbol}</span></div>
                <div className="sc-time">gemerkt {when(a.saved_at)} · {a.mode}</div>
                {meta && <div className="sc-meta mono">{meta}</div>}
              </div>
              <span className="mono ab-score">{Math.round(num(a.score) ?? 0)}</span>
            </label>
          );
        })}
      </div>
      {msg && <div className="sc-toast" role="alert"><span>{msg}</span></div>}
      {items.length > 0 && (
        <div className="sc-dock">
          <div className="sc-actions">
            <button disabled={!ids.length || busy} onClick={() => { if (window.confirm(`${ids.length} verwerfen?`)) run("verwerfen"); }}>Verwerfen</button>
            <button disabled={!ids.length || busy} onClick={() => run("uebernehmen")}>{ids.length} ins Journal</button>
          </div>
        </div>
      )}
    </>
  );
}
